import React, { useMemo } from "react";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import { GlobalButton } from "../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import { RECEPTIONIST_CALL_LOG_TABLE_HEADERS } from "../../constants/callLogDirectoryColumns";
import { formatCallDateDisplay } from "../../utils/callLogDates";

const STATUS_TONE_MAP = {
  resolved: "occl-status--resolved",
  pending: "occl-status--pending",
  cancelled: "occl-status--cancelled",
  completed: "occl-status--resolved",
  open: "occl-status--pending",
  "in progress": "occl-status--inprogress",
};

const HEADERS = RECEPTIONIST_CALL_LOG_TABLE_HEADERS;

const cellText = (value, maxLen = 80) => {
  const raw = value != null && value !== "" ? String(value) : "";
  if (!raw) return "—";
  if (raw.length <= maxLen) return raw;
  return `${raw.slice(0, maxLen)}…`;
};

const isRowCompleted = (log) =>
  log?.is_call_completed === 1 ||
  log?.is_call_completed === true ||
  String(log?.is_call_completed) === "1";

const ReceptionistCallLogsTable = ({
  logs,
  loading,
  onView,
  onMarkFollowUpCompleted,
  paginationMeta,
  onPageChange,
  columnVisibilityOverride = null,
  showPaginationFooter = true,
}) => {
  /* Show all columns by default; hide only when visibility map explicitly sets false (missing keys = visible). */
  const visibleHeaders = useMemo(() => {
    if (columnVisibilityOverride == null) return HEADERS;
    return HEADERS.filter((h) => {
      if (h.key === "actions") return true;
      return columnVisibilityOverride[h.key] !== false;
    });
  }, [columnVisibilityOverride]);

  const statusClass = (statusName) => {
    if (!statusName) return "";
    const key = statusName.toLowerCase().trim();
    return STATUS_TONE_MAP[key] || "occl-status--pending";
  };

  const formatTime = (timeString, dateString) => {
    if (timeString) return timeString;
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "#64748b" }}>
        Loading call logs...
      </div>
    );
  }

  return (
    <div>
      <GlobalDataTable
        headers={visibleHeaders}
        wrapClassName="occl-table-wrap"
        tableClassName="occl-table occl-table--compact occl-table--directory-wide"
        tableAriaLabel="Receptionist call logs table"
      >
        {!logs || logs.length === 0 ? (
          <tr>
            <td
              colSpan={visibleHeaders.length}
              style={{ textAlign: "center", padding: "3rem", color: "#94a3b8" }}
            >
              No records available
            </td>
          </tr>
        ) : (
          logs.map((log) => (
            <tr key={log.id}>
              {visibleHeaders.map((header) => {
                switch (header.key) {
                  case "call_date":
                    return <td key={header.key}>{formatCallDateDisplay(log.call_date)}</td>;
                  case "call_time":
                    return <td key={header.key}>{formatTime(log.call_time, log.call_date)}</td>;
                  case "caller":
                    return (
                      <td key={header.key}>
                        <span style={{ fontWeight: 600, color: "#0f172a" }}>{log.caller_name || "—"}</span>
                      </td>
                    );
                  case "contact_number":
                    return <td key={header.key}>{log.contact_number || "—"}</td>;
                  case "call_type":
                    return (
                      <td key={header.key}>
                        {log.call_type === true || log.call_type === 1 || String(log.call_type) === "1"
                          ? "Incoming"
                          : "Outgoing"}
                      </td>
                    );
                  case "purpose":
                    return <td key={header.key}>{log.purpose_name || "—"}</td>;
                  case "contact_person":
                    return (
                      <td key={header.key}>
                        {log.contact_person_name || log.handled_by_name || log.handled_by || "—"}
                      </td>
                    );
                  case "call_duration":
                    return (
                      <td key={header.key}>
                        {log.call_duration != null && log.call_duration !== "" ? `${log.call_duration} min` : "—"}
                      </td>
                    );
                  case "status":
                    return (
                      <td key={header.key}>
                        <span className={`occl-status-tag ${statusClass(log.status_name)}`}>
                          {log.status_name || "—"}
                        </span>
                      </td>
                    );
                  case "follow_up_date":
                    return (
                      <td key={header.key}>
                        {log.follow_up_date ? formatCallDateDisplay(log.follow_up_date) : "—"}
                      </td>
                    );
                  case "follow_up_assigned_to": {
                    if (!log.follow_up_date) return <td key={header.key}>—</td>;
                    const explicitAssignee =
                      log.follow_up_assigned_to != null && String(log.follow_up_assigned_to).trim() !== "";
                    const name = explicitAssignee ? log.follow_up_assignee_name : log.handled_by_name;
                    const role = explicitAssignee ? log.follow_up_assignee_role : log.handled_by_role;
                    const text = name && role ? `${name} (${role})` : name || "—";
                    return <td key={header.key}>{text}</td>;
                  }
                  case "action_taken": {
                    const t = cellText(log.action_taken, 100);
                    return (
                      <td key={header.key} title={log.action_taken || undefined} style={{ maxWidth: 220 }}>
                        {t}
                      </td>
                    );
                  }
                  case "remarks": {
                    const t = cellText(log.remarks, 100);
                    return (
                      <td key={header.key} title={log.remarks || undefined} style={{ maxWidth: 220 }}>
                        {t}
                      </td>
                    );
                  }
                  case "actions":
                    return (
                      <td key={header.key} className="occl-col-actions">
                        <div className="occl-actions">
                          <GlobalTableActionButton action="view" onClick={() => onView(log.id)} />
                          {log.follow_up_date && !isRowCompleted(log) ? (
                            <GlobalTableActionButton
                              action="followUpComplete"
                              title="Complete follow-up"
                              onClick={() => onMarkFollowUpCompleted?.(log.id)}
                            />
                          ) : null}
                        </div>
                      </td>
                    );
                  default:
                    return <td key={header.key}>—</td>;
                }
              })}
            </tr>
          ))
        )}
      </GlobalDataTable>

      {showPaginationFooter && paginationMeta && paginationMeta.total > 0 && (
        <div className="occl-pagination">
          <div className="occl-pagination-stats">
            Showing <strong>{logs?.length ?? 0}</strong> of <strong>{paginationMeta.total}</strong>{" "}
            records (Page <strong>{paginationMeta.page}</strong> of{" "}
            <strong>
              {Math.ceil(paginationMeta.total / paginationMeta.limit) || 1}
            </strong>
            ). Max <strong>{paginationMeta.limit}</strong> rows per page.
          </div>
          <div className="occl-pagination-controls">
            <GlobalButton
              variant="ghost"
              size="sm"
              disabled={paginationMeta.page <= 1}
              onClick={() => onPageChange(paginationMeta.page - 1)}
            >
              Previous
            </GlobalButton>
            <GlobalButton
              variant="ghost"
              size="sm"
              disabled={
                paginationMeta.page >=
                Math.ceil(paginationMeta.total / paginationMeta.limit)
              }
              onClick={() => onPageChange(paginationMeta.page + 1)}
            >
              Next
            </GlobalButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceptionistCallLogsTable;
